<script setup lang="ts">
const props = defineProps<{ profile: User }>()
const canvasRef = ref<HTMLCanvasElement | null>(null)

const W = 1200
const H = 630

// Reuse the server-rendered profile card (server/routes/og/profiles/[id].get.ts) —
// it's same-origin, so drawing it onto the canvas never taints it (unlike the
// user's avatar, which may live on a cross-origin storage host without CORS headers).
async function draw() {
  const canvas = canvasRef.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const img = new Image()
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error('Failed to load profile card image'))
    img.src = `/og/profiles/${props.profile.id}`
  })
  ctx.drawImage(img, 0, 0, W, H)
}

onMounted(() => draw())

// ── Actions ───────────────────────────────────────────────────────────────────

const fileName = () => `sailingmetrics-${props.profile.slug || props.profile.id}.png`

const download = () => {
  const a = document.createElement('a')
  a.download = fileName()
  a.href = canvasRef.value!.toDataURL('image/png')
  a.click()
}

const copied = ref(false)
const copyLink = async () => {
  await navigator.clipboard.writeText(`${window.location.origin}/profiles/${props.profile.slug}-${props.profile.id}`)
  copied.value = true
  setTimeout(() => { copied.value = false }, 2000)
}

const nativeShare = () => {
  canvasRef.value!.toBlob(async (blob) => {
    if (!blob) { download(); return }
    const file = new File([blob], fileName(), { type: 'image/png' })
    const payload = {
      title: `${props.profile.name}'s sailing profile`,
      text: `Check out ${props.profile.name}'s sailing profile on SailingMetrics`,
      url: `${window.location.origin}/profiles/${props.profile.slug}-${props.profile.id}`,
      files: [file],
    }
    if (navigator.canShare?.(payload)) {
      try { await navigator.share(payload); return } catch {}
    }
    download()
  })
}

const canNativeShare = ref(false)
onMounted(() => { canNativeShare.value = !!navigator.share })

defineExpose({ download, copyLink, nativeShare, copied, canNativeShare })
</script>

<template>
  <canvas ref="canvasRef" :width="W" :height="H" style="width: 100%; border-radius: 8px;" />
</template>
